import React from 'react'
import { Calendar, Clock, Users, MapPin, Image as ImageIcon, Circle } from 'lucide-react'
import { EventFormData } from '../EventCreationWizard'

interface EventPreviewCardProps {
  formData: EventFormData
}

const EventPreviewCard: React.FC<EventPreviewCardProps> = ({ formData }) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not set'
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatTime = (dateString: string) => {
    if (!dateString) return 'Not set'
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const formatTimeRange = (startString: string, endString: string) => {
    if (!startString || !endString) return 'Not set'
    return `${formatTime(startString)} - ${formatTime(endString)}`
  }

  return (
    <div className="bg-card rounded-xl shadow-lg overflow-hidden max-w-4xl mx-auto border border-border">
      {/* Event Image */}
      {formData.image_url ? (
        <div className="aspect-video w-full relative">
          <img
            src={formData.image_url}
            alt={formData.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        </div>
      ) : (
        <div className="aspect-video w-full bg-muted flex items-center justify-center">
          <ImageIcon className="text-muted-foreground" size={64} />
        </div>
      )}

      {/* Card Content */}
      <div className="p-4">
        {/* Event Title and Location */}
        <h3 className="text-4xl font-extrabold text-foreground tracking-tight">{formData.title || 'Untitled Event'}</h3>
        <p className="text-base text-muted-foreground flex items-center mt-1">
          <MapPin className="h-4 w-4 mr-2" />
          {formData.location_name || 'Location not set'}
        </p>

        {/* Metadata Grid */}
        <div className="mt-4 pt-4 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Date */}
          <div className="flex items-center space-x-3">
            <Calendar className="h-6 w-6 text-primary flex-shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Event Date</p>
              <p className="text-sm font-medium text-foreground">{formatDate(formData.event_date)}</p>
            </div>
          </div>

          {/* Pickup Time */}
          <div className="flex items-center space-x-3">
            <Clock className="h-6 w-6 text-primary flex-shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Pickup Window</p>
              <p className="text-sm font-medium text-foreground">
                {formatTimeRange(formData.pickup_start_time, formData.pickup_end_time)}
              </p>
            </div>
          </div>

          {/* Registration */}
          <div className="flex items-center space-x-3">
            <Users className="h-6 w-6 text-primary flex-shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Registration</p>
              <p className="text-sm font-medium text-foreground">
                {formatTimeRange(formData.registration_opens, formData.registration_closes)}
              </p>
            </div>
          </div>
        </div>

        {/* Status Badge */}
        <div className="mt-3">
          {formData.status === 'scheduled' ? (
            <div className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
              <Circle className="h-2 w-2 mr-2 fill-current" />
              Registration Open
            </div>
          ) : (
            <div className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-sm font-medium text-muted-foreground">
              <Circle className="h-2 w-2 mr-2 fill-current" />
              Draft
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EventPreviewCard
